import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

let csrfToken = null;

// Intercepteur pour injecter le jeton CSRF dans les requêtes de mutation
api.interceptors.request.use(
  (config) => {
    const method = config.method?.toUpperCase();
    if (csrfToken && ['POST', 'PUT', 'DELETE', 'PATCH'].includes(method)) {
      config.headers['X-CSRF-Token'] = csrfToken;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Intercepteur global pour attraper l'erreur 503 d'application non installée et stocker le CSRF token
api.interceptors.response.use(
  (response) => {
    const token = response.headers['x-csrf-token'];
    if (token) {
      csrfToken = token;
    }
    return response;
  },
  (error) => {
    const token = error.response?.headers?.['x-csrf-token'];
    if (token) {
      csrfToken = token;
    }

    const status = error.response?.status;
    const detail = error.response?.data?.detail || '';
    const errCode = error.response?.data?.error || '';
    const url = error.config?.url || '';

    // Détection de l'absence d'installation
    if (status === 503 && (errCode === 'installation_required' || detail.includes('non installee') || detail.includes('non installée'))) {
      // Éviter la redirection en boucle si on est déjà sur la page d'installation
      if (window.location.pathname !== '/install') {
        toast.error("L'application n'est pas encore installée. Redirection vers l'assistant d'installation dans quelques secondes...", {
          duration: 4000
        });
        setTimeout(() => {
          window.location.href = '/install';
        }, 3000);
      }
      return Promise.reject(error);
    }

    // Silencer les 401 sur /auth/me pour ne pas polluer la console navigateur
    if (status === 401 && url === '/auth/me') {
      return Promise.resolve({ data: null, status: 200 });
    }

    return Promise.reject(error);
  }
);

// Installation endpoints
export const installApi = {
  getStatus: () => api.get('/install/status'),
  install: (data) => api.post('/install', data),
};

// Public endpoints
export const publicApi = {
  getMenu: () => api.get('/public/menu'),
  getHours: () => api.get('/public/hours'),
  getPhotos: () => api.get('/public/photos'),
  getReviews: () => api.get('/public/reviews'),
  getSettings: () => api.get('/public/settings'),
  getActus: () => api.get('/public/actus'),
  getHomepageSections: () => api.get('/public/homepage-sections'),
  getMaintenanceInfo: () => api.get('/public/maintenance'),
  getVersion: () => api.get('/public/version'),
  createReservation: (data) => api.post('/public/reservations', data),
};

// Auth endpoints
export const authApi = {
  getConfig: () => api.get('/auth/config'),
  exchangeSession: (sessionId) => api.post('/auth/session', { session_id: sessionId }),
  getMe: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  requestPasswordReset: (data) => api.post('/auth/reset-password/request', data),
  resetPassword: (data) => api.post('/auth/reset-password/reset', data),
};

// Admin endpoints
export const adminApi = {
  getStats: () => api.get('/admin/stats'),
  // Categories
  getCategories: () => api.get('/admin/categories'),
  createCategory: (data) => api.post('/admin/categories', data),
  updateCategory: (id, data) => api.put(`/admin/categories/${id}`, data),
  deleteCategory: (id) => api.delete(`/admin/categories/${id}`),
  // Menu items
  getMenuItems: () => api.get('/admin/items'),
  createMenuItem: (data) => api.post('/admin/items', data),
  updateMenuItem: (id, data) => api.put(`/admin/items/${id}`, data),
  toggleMenuItem: (id) => api.patch(`/admin/items/${id}/toggle`),
  deleteMenuItem: (id) => api.delete(`/admin/items/${id}`),
  // Formules
  getFormules: () => api.get('/admin/formules'),
  createFormule: (data) => api.post('/admin/formules', data),
  updateFormule: (id, data) => api.put(`/admin/formules/${id}`, data),
  deleteFormule: (id) => api.delete(`/admin/formules/${id}`),
  // Hours
  getHours: () => api.get('/admin/hours'),
  updateHours: (data) => api.put('/admin/hours', data),
  createException: (data) => api.post('/admin/hours/exceptions', data),
  deleteException: (id) => api.delete(`/admin/hours/exceptions/${id}`),
  // Photos
  getPhotos: () => api.get('/admin/photos'),
  createPhoto: (data) => api.post('/admin/photos', data),
  uploadPhoto: (formData) => api.post('/admin/photos/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  updatePhoto: (id, data) => api.put(`/admin/photos/${id}`, data),
  deletePhoto: (id) => api.delete(`/admin/photos/${id}`),
  reorderPhotos: (data) => api.put('/admin/photos/reorder', data),
  // Reservations
  getReservations: () => api.get('/admin/reservations'),
  updateReservationStatus: (id, status) => api.put(`/admin/reservations/${id}/status`, { status }),
  // Settings
  getSettings: () => api.get('/admin/settings'),
  updateSettings: (data) => api.put('/admin/settings', data),
  // OCR menu
  parseOcrMenu: (formData) => api.post('/admin/ocr/parse-menu', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  saveOcrMenu: (data) => api.post('/admin/ocr/save-from-ocr', data),
  // Modules
  getModules: () => api.get('/admin/modules'),
  updateModules: (data) => api.put('/admin/modules', data),
  // Users (admin only)
  getUsers: () => api.get('/admin/users'),
  createUser: (data) => api.post('/admin/users', data),
  updateUser: (id, data) => api.put(`/admin/users/${id}`, data),
  deleteUser: (id) => api.delete(`/admin/users/${id}`),
  // Roles (admin only)
  getRoles: () => api.get('/admin/roles'),
  createRole: (data) => api.post('/admin/roles', data),
  updateRolePermissions: (id, permissions) => api.put(`/admin/roles/${id}/permissions`, permissions),
  deleteRole: (id) => api.delete(`/admin/roles/${id}`),
  // System (admin only)
  resetDemoData: () => api.post('/admin/system/reset-demo-data'),
  fullResetDatabase: (data) => api.post('/admin/system/full-reset', data),
  getEmailStatus: () => api.get('/admin/email-status'),
  sendTestEmail: () => api.post('/admin/email-test'),
  uploadActuImage: (formData) => api.post('/admin/actus/upload-image', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  // Homepage sections
  getHomepageSections: () => api.get('/admin/homepage-sections'),
  updateHomepageSection: (id, data) => api.put(`/admin/homepage-sections/${id}`, data),
  reorderHomepageSections: (data) => api.put('/admin/homepage-sections/reorder', data),
  uploadHomepageImage: (formData) => api.post('/admin/homepage/upload-image', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  // Formules
  uploadFormuleImage: (formData) => api.post('/admin/formules/upload-image', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  // SEO
  generateKeywords: (data) => api.post('/admin/seo/generate-keywords', data),
  // Favicon
  uploadFavicon: (formData) => api.post('/admin/favicon/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  // Logo
  uploadLogo: (formData) => api.post('/admin/logo/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteLogo: () => api.delete('/admin/logo'),
  // Reviews
  getReviews: () => api.get('/admin/reviews'),
  createReview: (data) => api.post('/admin/reviews', data),
  updateReview: (id, data) => api.put(`/admin/reviews/${id}`, data),
  deleteReview: (id) => api.delete(`/admin/reviews/${id}`),
  // Orders
  getOrders: () => api.get('/admin/orders'),
  updateOrder: (id, data) => api.patch(`/admin/orders/${id}`, data),
  // Actualités
  getActus: () => api.get('/admin/actus'),
  createActu: (data) => api.post('/admin/actus', data),
  updateActu: (id, data) => api.put(`/admin/actus/${id}`, data),
  deleteActu: (id) => api.delete(`/admin/actus/${id}`),
};

// Click & Collect public endpoints
export const clickCollectApi = {
  getSettings: () => api.get('/public/click-collect/settings'),
  getSlots: (date) => api.get(`/public/click-collect/slots?date=${date}`),
  createOrder: (data) => api.post('/public/orders', data),
  getOrderStatus: (orderId, sessionId) => api.get(`/public/orders/${orderId}/status${sessionId ? `?session_id=${sessionId}` : ''}`),
};

export default api;
